import type { UploadClientDocumentParams } from '@/domain/models/client.js';
import type { ThemeColors } from '@/domain/models/theme.js';
import type {
    CreateClient,
    CreateClientBtpEnv,
    CreateClientErpEnv,
    DeleteClientBtpEnv,
    DeleteClientDocument,
    DeleteClientErpEnv,
    GetClientDocumentDownloadUrl,
    ListClientBtpEnvs,
    ListClientDocuments,
    ListClientErpEnvs,
    LoadClientById,
    LoadClients,
    RemoveClientLogo,
    UpdateClient,
    UpdateClientBtpEnv,
    UpdateClientErpEnv,
    UploadClientDocument,
    UploadClientLogo
} from '@/domain/use-cases/clients.js';
import { BaseControllerImpl } from '@/presentation/controllers/base/implementation.js';

export class ClientController extends BaseControllerImpl {
    constructor(
        private readonly loadClientsUseCase: LoadClients,
        private readonly loadClientByIdUseCase: LoadClientById,
        private readonly createClientUseCase: CreateClient,
        private readonly updateClientUseCase: UpdateClient,
        private readonly uploadClientLogoUseCase: UploadClientLogo,
        private readonly removeClientLogoUseCase: RemoveClientLogo,
        private readonly listErpEnvsUseCase: ListClientErpEnvs,
        private readonly createErpEnvUseCase: CreateClientErpEnv,
        private readonly updateErpEnvUseCase: UpdateClientErpEnv,
        private readonly deleteErpEnvUseCase: DeleteClientErpEnv,
        private readonly listBtpEnvsUseCase: ListClientBtpEnvs,
        private readonly createBtpEnvUseCase: CreateClientBtpEnv,
        private readonly updateBtpEnvUseCase: UpdateClientBtpEnv,
        private readonly deleteBtpEnvUseCase: DeleteClientBtpEnv,
        private readonly listDocumentsUseCase: ListClientDocuments,
        private readonly uploadDocumentUseCase: UploadClientDocument,
        private readonly deleteDocumentUseCase: DeleteClientDocument,
        private readonly getDocumentDownloadUrlUseCase: GetClientDocumentDownloadUrl
    ) {
        super();
    }

    public async loadClients(search?: string) {
        return this.loadClientsUseCase.execute({ search });
    }

    public async loadClientById(id: string) {
        return this.loadClientByIdUseCase.execute(id);
    }

    public async createClient(params: {
        name: string;
        abbreviation: string;
        logoUrl?: string;
        aiModel?: string;
        slaHoursEstimateEf?: number;
        slaHoursStartDev?: number;
        slaHoursAnalyzeBug?: number;
        slaHoursAnalyzeMinor?: number;
        slaDaysValidateDoc?: number;
        slaMinScoreAi?: number;
        aiStrictnessLevel?: number;
        aiExtraSystemPrompt?: string;
        emailDomains?: string[];
        themeColors?: ThemeColors | null;
    }) {
        return this.createClientUseCase.execute(params);
    }

    public async updateClient(
        id: string,
        params: {
            name: string;
            abbreviation: string;
            logoUrl?: string;
            aiModel?: string;
            slaHoursEstimateEf?: number;
            slaHoursStartDev?: number;
            slaHoursAnalyzeBug?: number;
            slaHoursAnalyzeMinor?: number;
            slaDaysValidateDoc?: number;
            slaMinScoreAi?: number;
            aiStrictnessLevel?: number;
            aiExtraSystemPrompt?: string;
            emailDomains?: string[];
            themeColors?: ThemeColors | null;
        }
    ) {
        return this.updateClientUseCase.execute(id, params);
    }

    public async uploadLogo(clientId: string, params: { fileData: string; fileName: string; mimeType: string }) {
        return this.uploadClientLogoUseCase.execute(clientId, params);
    }

    public async removeLogo(clientId: string) {
        return this.removeClientLogoUseCase.execute(clientId);
    }

    public async listErpEnvironments(clientId: string) {
        return this.listErpEnvsUseCase.execute(clientId);
    }

    public async createErpEnvironment(clientId: string, params: { description: string; systemId: string; connectionAddress: string; instanceNumber: string }) {
        return this.createErpEnvUseCase.execute(clientId, params);
    }

    public async updateErpEnvironment(envId: string, params: { description?: string; systemId?: string; connectionAddress?: string; instanceNumber?: string }) {
        return this.updateErpEnvUseCase.execute(envId, params);
    }

    public async deleteErpEnvironment(envId: string) {
        return this.deleteErpEnvUseCase.execute(envId);
    }

    public async listBtpEnvironments(clientId: string) {
        return this.listBtpEnvsUseCase.execute(clientId);
    }

    public async createBtpEnvironment(
        clientId: string,
        params: { description: string; region: string; subaccountId: string; organization: string; space: string; hanaCloudUrl?: string | null }
    ) {
        return this.createBtpEnvUseCase.execute(clientId, params);
    }

    public async updateBtpEnvironment(
        envId: string,
        params: { description?: string; region?: string; subaccountId?: string; organization?: string; space?: string; hanaCloudUrl?: string | null }
    ) {
        return this.updateBtpEnvUseCase.execute(envId, params);
    }

    public async deleteBtpEnvironment(envId: string) {
        return this.deleteBtpEnvUseCase.execute(envId);
    }

    public async listDocuments(clientId: string) {
        return this.listDocumentsUseCase.execute(clientId);
    }

    public async uploadDocument(clientId: string, params: UploadClientDocumentParams) {
        return this.uploadDocumentUseCase.execute(clientId, params);
    }

    public async deleteDocument(clientId: string, docId: string) {
        return this.deleteDocumentUseCase.execute(clientId, docId);
    }

    public async getDocumentDownloadUrl(clientId: string, docId: string) {
        return this.getDocumentDownloadUrlUseCase.execute(clientId, docId);
    }
}
